import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import PaymentPanel from './PaymentPanel'
import DateHelpers from '../Helper/DateHelpers'
import '../Styles/BalancePayment.scss'

// Pays down balances on registrations that already exist — one card entry across as many as the
// user checks, instead of the old per-card Add Payment button.
//
// Every amount comes from /cart/balance/quote. The browser sends registration ids and nothing else,
// which is what stops a client from deciding what it owes.
function BalancePayment({ onPaid }) {

   const navigate = useNavigate()
   const [lines, setLines] = useState([])
   const [selected, setSelected] = useState({})
   const [loading, setLoading] = useState(true)
   const [busy, setBusy] = useState(false)
   const [error, setError] = useState('')

   const [clientSecret, setClientSecret] = useState('')
   const [paymentIntentId, setPaymentIntentId] = useState('')
   const [isPaying, setIsPaying] = useState(false)

   const asDollars = (cents) => (cents / 100).toFixed(2)

   const loadBalances = useCallback(async () => {
      setLoading(true)
      try {
         const response = await fetch('/cart/balance/quote', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
         })
         const data = await response.json()
         if (!response.ok) {
            setError(data.error ?? 'Could not load your balances.')
            return
         }
         setLines(data.lines ?? [])
         // Checked by default, the way Amazon pre-selects everything in a cart.
         const initial = {}
         ;(data.lines ?? []).forEach(line => { initial[line.regist_id] = true })
         setSelected(initial)
      } catch {
         setError('Could not load your balances.')
      } finally {
         setLoading(false)
      }
   }, [])

   useEffect(() => {
      loadBalances()
   }, [loadBalances])

   function toggle(registId) {
      // Changing the selection invalidates any payment already started — the amount would be stale.
      setClientSecret('')
      setPaymentIntentId('')
      setSelected({ ...selected, [registId]: !selected[registId] })
   }

   const selectedLines = lines.filter(line => selected[line.regist_id])
   const totalCents = selectedLines.reduce((sum, line) => sum + line.amount_due_cents, 0)

   async function startPayment() {
      setBusy(true)
      setError('')
      try {
         const response = await fetch('/cart/balance/checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ registIds: selectedLines.map(l => l.regist_id) }),
         })
         const data = await response.json()
         if (!response.ok) {
            setError(data.error ?? 'Could not start payment.')
            return
         }
         setClientSecret(data.clientSecret)
         setPaymentIntentId(data.paymentIntentId)
      } catch {
         setError('Could not start payment.')
      } finally {
         setBusy(false)
      }
   }

   async function handlePaid() {
      setIsPaying(true)
      try {
         const response = await fetch('/cart/balance/finalize', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ paymentIntentId }),
         })
         const data = await response.json()
         if (!response.ok) {
            setError(data.error ?? 'Your payment succeeded but we could not update your balances. Please contact the office.')
            setIsPaying(false)
            return
         }
         navigate('/daycamppaymentsuccess', {
            state: {
               paymentAmount: asDollars(data.amount_paid_cents ?? 0),
               registrationCount: data.registrations?.length ?? 0,
            },
         })
         onPaid?.()
      } catch {
         setError('Your payment succeeded but we could not update your balances. Please contact the office.')
         setIsPaying(false)
      }
   }

   if (loading) {
      return <div className="BalancePaymentLoading">Loading your balances…</div>
   }

   // On its own page this has to say something rather than render nothing.
   if (lines.length === 0) {
      return (
         <div className="BalancePaymentContainer">
            <div className="BalancePaymentHeading">Make a Payment</div>
            <div className="BalancePaymentEmpty">
               <p>You have no balances due. Every registration on your account is paid in full.</p>
               <button type="button" onClick={() => navigate('/individualaccount')}>
                  Back to my account
               </button>
            </div>
         </div>
      )
   }

   return (
      <div className="BalancePaymentContainer">
         <div className="BalancePaymentHeading">Make a Payment</div>
         <div className="BalancePaymentIntro">
            Choose which registrations to pay for. They will be paid together in one charge.
         </div>

         {error && <div className="BalancePaymentError">{error}</div>}

         <div className="BalancePaymentList">
            {lines.map(line => (
               <label className="BalancePaymentLine" key={line.regist_id}>
                  <input
                     type="checkbox"
                     checked={Boolean(selected[line.regist_id])}
                     disabled={busy || isPaying}
                     onChange={() => toggle(line.regist_id)} />
                  <div className="BalancePaymentLineInfo">
                     <div className="BalancePaymentLineEvent">{line.camp_name}</div>
                     <div className="BalancePaymentLineCamper">
                        {line.first_name} {line.last_name} · {DateHelpers.getEventDateRange(line.begin_date, line.end_date)}
                     </div>
                  </div>
                  <div className="BalancePaymentLineAmount">${asDollars(line.amount_due_cents)}</div>
               </label>
            ))}
         </div>

         <div className="BalancePaymentTotal">
            <div>Total selected</div>
            <div>${asDollars(totalCents)}</div>
         </div>

         {!clientSecret && (
            <div className="BalancePaymentActions">
               <button
                  type="button"
                  disabled={busy || selectedLines.length === 0}
                  onClick={startPayment}>
                  Make a payment
               </button>
            </div>
         )}

         {clientSecret && (
            <PaymentPanel
               clientSecret={clientSecret}
               totalCents={totalCents}
               lines={selectedLines.map(line => ({
                  key: line.regist_id,
                  label: `${line.camp_name} — ${line.first_name} ${line.last_name}`,
                  amountCents: line.amount_due_cents,
               }))}
               onPaid={handlePaid}
               onProcessingChange={setIsPaying}
               busyLabel={isPaying ? 'Updating your balances…' : ''} />
         )}
      </div>
   )
}

export default BalancePayment
